// Accessor for the bundled COVID testing/positivity snapshot.
//
// The data ships with the app (src/data/covid-data.json, regenerated by
// `npm run fetch-data`) rather than being fetched at runtime. It is a fixed OWID
// snapshot that will not change again, and the dashboard previously went blank
// for everyone when its CKAN host disappeared. Bundling also cuts transfer: all
// 56 regions gzip to ~62 KB, where fetching just the two default countries used
// to cost ~343 KB across four round-trips.

import dataset from './covid-data.json';

// Parse as local midnight. The chart tooltip reads Date.toString(), so these
// must stay Date objects rather than strings.
const parseDay = (s) => {
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, m - 1, d);
};

export const DATA_START = parseDay(dataset.start);
export const DATA_END = parseDay(dataset.end);

// The generator guarantees a contiguous daily grid, so index == days since start.
const ALL_DAYS = Array.from({ length: dataset.days }, (_, i) => {
    const d = new Date(DATA_START);
    d.setDate(d.getDate() + i);
    return d;
});

const hasValues = (arr) => arr.some((v) => v !== null);

// 22 of the 56 regions in the source (Egypt, Algeria, Tanzania, the "Africa"
// aggregate...) carry rows but no readings at all. Listing them would render
// empty charts that look like a broken dashboard, so the picker only offers
// regions with something to plot.
export const REGIONS = Object.keys(dataset.regions)
    .filter((r) => hasValues(dataset.regions[r].new_tests) || hasValues(dataset.regions[r].positive_rate))
    .map((r) => ({ iso: dataset.regions[r].iso, region: r }))
    .sort((a, b) => a.region.localeCompare(b.region));

// Date windows are measured back from the last day of DATA, not from today.
// The snapshot ends in March 2022, so anchoring to `new Date()` put every
// window past the end of the data and drew nothing.
const startIndex = (months) => {
    if (!months) return 0;
    const from = new Date(DATA_END);
    from.setMonth(from.getMonth() - months);
    const i = ALL_DAYS.findIndex((d) => d >= from);
    return i < 0 ? 0 : i;
};

// months = 6 | 12, or null/undefined for the full range.
export const getDates = (months) => ALL_DAYS.slice(startIndex(months));

export const getValues = (region, metric, months) => {
    const entry = dataset.regions[region];
    if (!entry || !entry[metric]) return [];
    // echarts leaves a gap for '' but would plot null as zero.
    return entry[metric].slice(startIndex(months)).map((v) => (v === null ? '' : v));
};

export const dumpUrl = (metric) =>
    `https://ckan.africadatahub.org/datastore/dump/${dataset.resources[metric]}?bom=True`;
